import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { useFormState } from 'react-final-form';
import {
  containerPath,
  DESTINATION,
  EntityLink,
  layoutPath,
  OFFER_BLOCK,
  OfferVariationsList,
  offerPath,
  PopoverFact,
  RefPickerField,
  typeLabel,
  type OfferVariation,
} from '../../../features/stackBuilder';
import { resolveTemplate, TEMPLATE_DEFAULTS, type StackItem } from './sequenceModel';
import { useOfferBlockByOfferId } from './queries';

/* eslint-disable @typescript-eslint/no-explicit-any -- API entities */
function BlockMembers({ item }: { item: StackItem }) {
  const inline = item.offer.offerBlock?.offerBlockOffers;
  const { data, isLoading } = useOfferBlockByOfferId(inline?.length ? null : item.offer.id);
  const members: any[] = inline?.length ? inline : (data?.offerBlockOffers ?? []);
  if (isLoading && !inline?.length)
    return (
      <Typography variant='body2' color='text.secondary'>
        Loading the block's offers
      </Typography>
    );
  return (
    <Box>
      <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
        {`Offers in this block (${members.length})`}
      </Typography>
      <List
        dense
        disablePadding
        sx={{ border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.default' }}
      >
        {members.map((member, index) => (
          <ListItem key={`${member?.offer?.id}-${index}`} divider={index < members.length - 1} sx={{ gap: 1, pl: 0.5 }}>
            {member?.offer?.id != null && (
              <EntityLink to={offerPath(member.offer.id)} label={`Open offer ${member.offer.id} in a new tab`} />
            )}
            <ListItemText
              primary={`${member?.offer?.id}: ${member?.offer?.name ?? ''}`}
              secondary={member?.offer?.offerType?.name}
            />
          </ListItem>
        ))}
      </List>
      <Typography variant='caption' color='text.secondary'>
        The order inside a block is edited on the offer block screen.
      </Typography>
    </Box>
  );
}

/** The popover body for one stack row: what it is, and the layout/container it renders with. */
export default function SequenceOfferDetails({
  item,
  index,
  layouts,
  containers,
  offerTypesByName,
  variations,
}: {
  item: StackItem;
  index: number;
  layouts: any[] | undefined;
  containers: any[] | undefined;
  offerTypesByName: Record<string, any> | undefined;
  variations: OfferVariation[] | null;
}) {
  const { values } = useFormState({ subscription: { values: true } });
  const type = item.offer.offerType?.name;

  if (type === DESTINATION) {
    return (
      <>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
          <PopoverFact label='Offer action'>
            {item.offer.offerActionId ?? item.offer.offerAction?.id ?? '—'}
          </PopoverFact>
          <PopoverFact label='Action type'>
            {item.offer.offerActionType ?? item.offer.offerAction?.offerActionType ?? type}
          </PopoverFact>
        </Box>
        <Alert severity='info' variant='outlined'>
          Destinations are configured on the Offer Actions screen; only their position is set here. A Destination sends
          the user away in the same tab, so the rows after it are reached only when it is skipped.
        </Alert>
      </>
    );
  }

  if (type === OFFER_BLOCK) return <BlockMembers item={item} />;

  const hasDefaults = TEMPLATE_DEFAULTS.some((d) => d.offerType === type);
  const typeId = offerTypesByName?.[type ?? '']?.id;
  const typeContainers = containers?.filter((container) => typeId != null && container.offerTypeHint === typeId);
  const fallback = (which: 'layout' | 'container') => {
    const resolved = resolveTemplate({ ...item, layoutOverride: null, containerOverride: null }, values, which);
    return resolved.ref?.name ? `Sequence default: ${resolved.ref.name}` : 'No sequence default for this type';
  };

  return (
    <>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
        <PopoverFact label='Offer type'>{typeLabel(item.offer) || '—'}</PopoverFact>
        <PopoverFact label='Advertiser'>{item.offer.advertiser?.name ?? 'None'}</PopoverFact>
      </Box>
      <OfferVariationsList variations={variations} />
      <RefPickerField
        name={`stack[${index}].layoutOverride`}
        label='Layout override'
        placeholder={hasDefaults ? 'Use the sequence default' : undefined}
        options={layouts}
        linkTo={(layout) => layoutPath(layout.id)}
        helperText={fallback('layout')}
      />
      <RefPickerField
        name={`stack[${index}].containerOverride`}
        label='Container override'
        placeholder={hasDefaults ? 'Use the sequence default' : undefined}
        options={typeContainers}
        linkTo={(container) => containerPath(container.id)}
        helperText={fallback('container')}
      />
    </>
  );
}
